import { AdvancedFilterData } from 'components/filter/advancedFilter/FilterFormController';
import { UserFilterSet, PartialFilterData } from 'models/filterSet';
import {
  doesCurrentStateMatchFilterSet,
  getDefaultFilterValues,
  resetAndApplyFilterSet,
  mergeFilterSet,
  replaceWithFilterSet,
  isFilterAtDefault,
  areAllFiltersAtDefaults,
  extractChangedFilters,
  mergeWithCurrentFilters,
} from './filterDefaults';

export interface FilterSetMatchResult {
  /** Whether current values exactly match the filter set */
  isExactMatch: boolean;
  /** Whether the filter set is currently selected */
  isSelected: boolean;
  /** Whether any filter set is currently active */
  hasActiveFilterSet: boolean;
  /** Whether there are conflicts preventing direct application */
  hasConflicts: boolean;
  /** Matching filter set if any */
  matchingSet?: UserFilterSet;
}

export interface FilterSetApplicationOptions {
  /** How to handle conflicts when applying filter set */
  conflictResolution: 'merge' | 'replace' | 'prompt';
  /** Whether to reset other filters to defaults when switching filter sets */
  resetOnSwitch: boolean;
}

/**
 * Utility class for managing filter set matching and application logic.
 * Encapsulates common patterns used across filter set components.
 */
export class FilterSetMatcher {
  constructor(
    private filterSets: UserFilterSet[],
    private selectedFilterSetId: string | null
  ) {}

  /**
   * Analyzes the current filter state against available filter sets
   */
  analyzeCurrentState(currentValues: AdvancedFilterData): FilterSetMatchResult {
    const matchingSet = this.filterSets.find(set => doesCurrentStateMatchFilterSet(currentValues, set.content));

    const isExactMatch = Boolean(matchingSet);
    const isSelected = Boolean(
      this.selectedFilterSetId && this.filterSets.some(set => set.filterSetId === this.selectedFilterSetId)
    );
    const hasActiveFilterSet = isExactMatch;

    const hasConflicts = this.filterSets.some(set => {
      if (!this.selectedFilterSetId || set.filterSetId !== this.selectedFilterSetId) {
        return false;
      }
      return Object.keys(set.content).some(key => {
        const typedKey = key as keyof AdvancedFilterData;
        return !isFilterAtDefault(typedKey, currentValues[typedKey]);
      });
    });

    return {
      isExactMatch,
      isSelected,
      hasActiveFilterSet,
      hasConflicts,
      matchingSet,
    };
  }

  /**
   * Determines how to apply a filter set based on current state
   */
  determineApplicationStrategy(
    filterSetId: string,
    currentValues: AdvancedFilterData
  ): {
    action: 'deactivate' | 'switch' | 'merge' | 'apply' | 'prompt';
    requiresUserConfirmation: boolean;
    newValues?: AdvancedFilterData;
  } {
    const filterSet = this.filterSets.find(set => set.filterSetId === filterSetId);
    if (!filterSet) {
      throw new Error(`Filter set with ID ${String(filterSetId)} not found`);
    }

    const analysis = this.analyzeCurrentState(currentValues);

    if (
      this.selectedFilterSetId === filterSetId &&
      analysis.isExactMatch &&
      analysis.matchingSet?.filterSetId === filterSetId
    ) {
      return {
        action: 'deactivate',
        requiresUserConfirmation: false,
        newValues: getDefaultFilterValues(),
      };
    }

    if (analysis.hasActiveFilterSet && analysis.matchingSet?.filterSetId !== filterSetId) {
      return {
        action: 'switch',
        requiresUserConfirmation: false,
        newValues: resetAndApplyFilterSet(filterSet.content),
      };
    }

    // Check if any filter in the set has non-default values
    const hasNonDefaultFiltersInSet = Object.keys(filterSet.content).some(key => {
      const typedKey = key as keyof AdvancedFilterData;
      return !isFilterAtDefault(typedKey, currentValues[typedKey]);
    });

    if (hasNonDefaultFiltersInSet) {
      // Requires user decision on how to handle conflicts
      return {
        action: 'prompt',
        requiresUserConfirmation: true,
      };
    }

    // Safe to apply directly
    return {
      action: 'apply',
      requiresUserConfirmation: false,
      newValues: mergeFilterSet(currentValues, filterSet.content),
    };
  }

  /**
   * Applies a filter set with the specified conflict resolution strategy
   */
  applyFilterSet(
    filterSetId: string,
    currentValues: AdvancedFilterData,
    strategy: 'merge' | 'replace'
  ): AdvancedFilterData {
    const filterSet = this.filterSets.find(set => set.filterSetId === filterSetId);
    if (!filterSet) {
      throw new Error(`Filter set with ID ${String(filterSetId)} not found`);
    }

    switch (strategy) {
      case 'merge':
        return mergeFilterSet(currentValues, filterSet.content);
      case 'replace':
        return replaceWithFilterSet(currentValues, filterSet.content);
      default:
        throw new Error(`Unknown strategy: ${String(strategy)}`);
    }
  }

  /**
   * Gets the display state for a filter set in the UI
   */
  getFilterSetDisplayState(
    filterSetId: string,
    currentValues: AdvancedFilterData
  ): {
    isActive: boolean;
    isSelected: boolean;
    status: 'active' | 'selected' | 'available';
    tooltip: string;
  } {
    const filterSet = this.filterSets.find(set => set.filterSetId === filterSetId);
    if (!filterSet) {
      throw new Error(`Filter set with ID ${String(filterSetId)} not found`);
    }

    const matchesCurrentFilters = doesCurrentStateMatchFilterSet(currentValues, filterSet.content);
    const isActive = this.selectedFilterSetId === filterSetId && matchesCurrentFilters;
    const isSelected = this.selectedFilterSetId === filterSetId && !matchesCurrentFilters;

    // Build tooltip description
    const savedFilterKeys = Object.keys(filterSet.content);
    let tooltip = savedFilterKeys.length > 0 ? `Saved filters: ${savedFilterKeys.join(', ')}` : 'No filters saved';

    if (isActive) {
      tooltip += '\n(matches current filters)';
    } else if (isSelected) {
      tooltip += '\n(partially applied)';
    }

    let status: 'active' | 'selected' | 'available';
    if (isActive) {
      status = 'active';
    } else if (isSelected) {
      status = 'selected';
    } else {
      status = 'available';
    }

    return {
      isActive,
      isSelected,
      status,
      tooltip,
    };
  }

  /**
   * Checks if all current filters are at their default values
   */
  static areFiltersAtDefaults(currentValues: AdvancedFilterData): boolean {
    return areAllFiltersAtDefaults(currentValues);
  }

  /**
   * Finds the filter set that matches current values, if any
   */
  findMatchingFilterSet(currentValues: AdvancedFilterData): UserFilterSet | null {
    return this.filterSets.find(set => doesCurrentStateMatchFilterSet(currentValues, set.content)) ?? null;
  }

  /**
   * Updates the internal state with new filter sets and selected filter set ID
   */
  static updateState(filterSets: UserFilterSet[], selectedFilterSetId: string | null): FilterSetMatcher {
    return new FilterSetMatcher(filterSets, selectedFilterSetId);
  }

  /**
   * Extracts only the filter fields that differ from their default values.
   * Static utility method for creating minimal filter sets.
   *
   * @param filterValues - Current filter values to analyze
   * @returns PartialFilterData containing only the fields that have changed from defaults
   */
  static extractChangedFilters(filterValues: AdvancedFilterData): PartialFilterData {
    return extractChangedFilters(filterValues);
  }

  /**
   * Merges saved partial filter data with current filter values.
   * Static utility method for applying filter sets.
   *
   * @param currentValues - Current filter state
   * @param savedFilters - Partial filter data from saved filter set
   * @returns Complete AdvancedFilterData with saved values merged in
   */
  static mergeWithCurrentFilters(
    currentValues: AdvancedFilterData,
    savedFilters: PartialFilterData
  ): AdvancedFilterData {
    return mergeWithCurrentFilters(currentValues, savedFilters);
  }

  /**
   * Checks if a single filter field is at its default value.
   * Static utility method for validation and state checking.
   *
   * @param filterKey - The filter field to check
   * @param currentValue - Current value of the filter field
   * @returns true if the current value matches the default value for this field
   */
  static isFilterAtDefault(filterKey: keyof AdvancedFilterData, currentValue: unknown): boolean {
    return isFilterAtDefault(filterKey, currentValue);
  }
}

/**
 * Factory function to create a FilterSetMatcher instance
 */
export const createFilterSetMatcher = (
  filterSets: UserFilterSet[],
  selectedFilterSetId: string | null
): FilterSetMatcher => new FilterSetMatcher(filterSets, selectedFilterSetId);
